// Zod DTOs for the `/v1/simulations` R2 simulation-bundle upload service
// (docs/design/simulation-upload-design.md §3 "Upload mechanism" + §8 Wave 2
// task 4). Mirrors `./asset.ts`'s three-step "presign -> direct PUT ->
// finalise" wire contract shape, widened to a MANIFEST of files (one bundle
// = many objects under one `SimulationBundle.id`-derived R2 prefix) instead
// of a single object:
//
//   POST /v1/simulations                    { folderName?, files: [{path,sizeBytes}] }
//     -> { bundleId, expiresIn: 900, uploads: [{ path, url }] }, row = PENDING
//   POST /v1/simulations/:bundleId/upload-urls  {}  (re-mint, still-PENDING only)
//     -> { expiresIn: 900, uploads: [{ path, url }] } — missing manifest paths only
//   POST /v1/simulations/:bundleId/finalise  {}
//     -> the finalised SimulationBundle (READY), or 422 on verification failure
//
// `totalBytes` is `BigInt` on the Prisma row (SQLite/D1 INTEGER is 64-bit) —
// carried as a decimal STRING on the wire, same convention as
// `./asset.ts`'s `assetResponseSchema.sizeBytes`.
import { z } from 'zod';
import {
  SIMULATION_BUNDLE_MAX_FILE_COUNT,
  SIMULATION_BUNDLE_MAX_PATH_LENGTH,
} from '../simulationBundleAllowlist';

const manifestPathSchema = z.string().trim().min(1).max(SIMULATION_BUNDLE_MAX_PATH_LENGTH);
const manifestSizeBytesSchema = z.number().int().positive();

/** A caller-declared display name for the uploaded folder — DISPLAY ONLY,
 * never structural (design doc §4.1's `originalFolderName` header: folder
 * casing is inconsistent across OS/browsers and must never drive a key or
 * lookup). */
const folderNameSchema = z.string().trim().min(1).max(255).optional();

export const simulationBundleManifestFileSchema = z.object({
  path: manifestPathSchema,
  sizeBytes: manifestSizeBytesSchema,
});
export type SimulationBundleManifestFile = z.infer<typeof simulationBundleManifestFileSchema>;

// ─── POST /v1/simulations ───────────────────────────────────────────────────

export const simulationBundleCreateRequestSchema = z.object({
  folderName: folderNameSchema,
  files: z
    .array(simulationBundleManifestFileSchema)
    .min(1, 'files must contain at least one entry')
    .max(
      SIMULATION_BUNDLE_MAX_FILE_COUNT,
      `files must not exceed ${SIMULATION_BUNDLE_MAX_FILE_COUNT} entries`,
    ),
});
export type SimulationBundleCreateRequest = z.infer<typeof simulationBundleCreateRequestSchema>;

export const simulationBundleUploadEntrySchema = z.object({
  path: z.string(),
  url: z.string(),
});
export type SimulationBundleUploadEntry = z.infer<typeof simulationBundleUploadEntrySchema>;

export const simulationBundleCreateResponseSchema = z.object({
  bundleId: z.string(),
  expiresIn: z.number().int().positive(),
  uploads: z.array(simulationBundleUploadEntrySchema),
});
export type SimulationBundleCreateResponse = z.infer<typeof simulationBundleCreateResponseSchema>;

// ─── POST /v1/simulations/:bundleId/upload-urls (re-mint) ──────────────────
// Empty body — the route re-derives which manifest paths are still missing
// from R2's own `list()` (never client-supplied), matching `./asset.ts`'s
// "server derives, never trusts the client" posture throughout.
export const simulationBundleUploadUrlsRequestSchema = z.object({}).strict();
export type SimulationBundleUploadUrlsRequest = z.infer<
  typeof simulationBundleUploadUrlsRequestSchema
>;

export const simulationBundleUploadUrlsResponseSchema = z.object({
  expiresIn: z.number().int().positive(),
  uploads: z.array(simulationBundleUploadEntrySchema),
});
export type SimulationBundleUploadUrlsResponse = z.infer<
  typeof simulationBundleUploadUrlsResponseSchema
>;

// ─── POST /v1/simulations/:bundleId/finalise ────────────────────────────────
// Empty body — same rationale as upload-urls above (finalise verifies
// against R2's own `list()` + real object bytes, never a client-supplied
// checksum/size).
export const simulationBundleFinaliseRequestSchema = z.object({}).strict();
export type SimulationBundleFinaliseRequest = z.infer<
  typeof simulationBundleFinaliseRequestSchema
>;

// ─── SimulationBundle response (create/finalise/re-mint's row projection) ──

const simulationBundleStatusSchema = z.enum(['PENDING', 'READY', 'FAILED']);

export const simulationBundleResponseSchema = z.object({
  id: z.string(),
  status: simulationBundleStatusSchema,
  entryPath: z.string(),
  fileCount: z.number().int(),
  // BigInt -> decimal string, see file header.
  totalBytes: z.string(),
  originalFolderName: z.string().nullable(),
  failureReason: z.string().nullable(),
  uploadedById: z.string(),
  createdAt: z.string().datetime(),
  readyAt: z.string().datetime().nullable(),
});
export type SimulationBundleResponse = z.infer<typeof simulationBundleResponseSchema>;
